import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { api } from '../api';

function getHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('adminToken')}` };
}

const VENDORS = ['Microsoft', 'Google', 'Mozilla', 'Adobe', 'Dell', 'HP'];
const OS_TYPES = ['windows', 'linux-debian', 'linux-rhel'];
const CATEGORIES = ['os', 'browser', 'application', 'driver', 'bios', 'firmware'];
const SEVERITIES = ['critical', 'high', 'medium', 'low'];

const SEV_COLORS = {
  critical: { bg: '#fee2e2', color: '#dc2626' },
  high:     { bg: '#ffedd5', color: '#ea580c' },
  medium:   { bg: '#fef3c7', color: '#d97706' },
  low:      { bg: '#dcfce7', color: '#16a34a' },
};

export default function Patches() {
  const [patches,   setPatches]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [filter,    setFilter]    = useState('');
  const [showForm,  setShowForm]  = useState(false);
  const [form,      setForm]      = useState({ name: '', vendor: '', product: '', version: '', osType: 'windows', category: 'application', severity: 'medium', downloadUrl: '', command: '', description: '' });
  const [saving,    setSaving]    = useState(false);
  const [fetching,  setFetching]  = useState(false);
  const [msg,       setMsg]       = useState('');
  const [msgType,   setMsgType]   = useState('success');

  // Deploy to device state
  const [deployPatch,   setDeployPatch]   = useState(null);  // patch being deployed
  const [devices,       setDevices]       = useState([]);
  const [selDevIds,     setSelDevIds]     = useState([]);
  const [deploying,     setDeploying]     = useState(false);
  const [deployResults, setDeployResults] = useState(null);  // {success:[],failed:[]}

  function flash(text, type = 'success') {
    setMsg(text); setMsgType(type);
    setTimeout(() => setMsg(''), 6000);
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.getAllPatches();
      setPatches(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.createPatch(form);
      setMsg('Patch created.'); setMsgType('success');
      setShowForm(false);
      setForm({ name: '', vendor: '', product: '', version: '', osType: 'windows', category: 'application', severity: 'medium', downloadUrl: '', command: '', description: '' });
      load();
    } catch (err) {
      setMsg('Error: ' + (err.response?.data?.error || err.message)); setMsgType('error');
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(''), 5000);
    }
  }

  async function fetchUpdates() {
    setFetching(true);
    flash('Checking all vendors for new patch releases...', 'info');
    try {
      await api.fetchPatchUpdates();
      setTimeout(async () => {
        await load();
        flash('Patch catalog updated from all vendor sources.');
        setFetching(false);
      }, 8000);
    } catch (err) {
      flash('Fetch failed: ' + (err.response?.data?.error || err.message), 'error');
      setFetching(false);
    }
  }

  // ── Open deploy modal for a patch ─────────────────────────────────────────
  async function openDeploy(patch) {
    setDeployPatch(patch);
    setSelDevIds([]);
    setDeployResults(null);
    try {
      const [invRes, statusRes] = await Promise.all([
        axios.get('/api/agents/inventory', { headers: getHeaders() }),
        axios.get('/api/patches/compliance', { headers: getHeaders() }).catch(() => ({ data: { details: [] } })),
      ]);

      const allDevices  = invRes.data?.devices  || [];
      const allStatuses = statusRes.data?.details || [];

      // Devices that already have this patch successfully applied
      const alreadyDone = new Set(
        allStatuses
          .filter(s => s.patchId === patch.id && s.status === 'success')
          .map(s => s.deviceId)
      );

      const needsPatch = allDevices.filter(d => {
        // Skip if already successfully patched
        if (alreadyDone.has(d.id)) return false;

        // OS patch: check if device is already on target version
        if (patch.category === 'os') {
          const devVer = (d.osVersion || '').toUpperCase();
          const patchVer = (patch.version || '').toUpperCase();
          if (devVer.includes(patchVer)) return false;
        }

        // OS type filter — don't show Linux devices for Windows patches
        if (patch.osType && patch.osType !== 'all') {
          const devOs = (d.os || '').toLowerCase();
          if (patch.osType === 'windows' && !devOs.includes('windows')) return false;
          if (patch.osType.includes('linux') && devOs.includes('windows')) return false;
        }

        // Browser/Application: check software inventory for installed version
        // If device has the app installed at same or newer version → skip
        if ((patch.category === 'browser' || patch.category === 'application') && patch.product) {
          const patchProduct = patch.product.toLowerCase();
          const patchVersion = patch.version || '';
          const inv = d.softwareInventory || [];
          const installed = inv.find(s =>
            (s.product || '').toLowerCase().includes(patchProduct) ||
            patchProduct.includes((s.product || '').toLowerCase().split(' ')[0])
          );
          if (installed && installed.version) {
            // Compare versions — skip if installed >= patch version
            const cmp = compareVer(installed.version, patchVersion);
            if (cmp >= 0) return false; // already up to date
          }
        }

        return true;
      });

      setDevices(needsPatch);
      // Auto-select all online devices that need this patch
      setSelDevIds(needsPatch.filter(d => d.status === 'online').map(d => d.id));
    } catch (e) {
      // Fallback: show all devices
      const res = await axios.get('/api/agents', { headers: getHeaders() }).catch(() => ({ data: [] }));
      const all = res.data || [];
      setDevices(all);
      setSelDevIds(all.filter(d => d.status === 'online').map(d => d.id));
    }
  }

  // Simple version comparison: returns -1, 0, or 1
  function compareVer(a, b) {
    if (!a || !b) return 0;
    const pa = String(a).split('.').map(n => parseInt(n) || 0);
    const pb = String(b).split('.').map(n => parseInt(n) || 0);
    const len = Math.max(pa.length, pb.length);
    for (let i = 0; i < len; i++) {
      if ((pa[i]||0) > (pb[i]||0)) return 1;
      if ((pa[i]||0) < (pb[i]||0)) return -1;
    }
    return 0;
  }

  function closeDeploy() {
    setDeployPatch(null);
    setSelDevIds([]);
    setDeployResults(null);
  }

  // ── Deploy patch to selected devices ──────────────────────────────────────
  async function submitDeploy() {
    if (!deployPatch || selDevIds.length === 0) return;
    setDeploying(true);
    const success = [], failed = [];
    for (const deviceId of selDevIds) {
      try {
        await axios.post('/api/tasks/assign',
          { deviceId, patchId: deployPatch.id },
          { headers: getHeaders() }
        );
        const d = devices.find(x => x.id === deviceId);
        success.push(d?.hostname || deviceId);
      } catch (e) {
        const d = devices.find(x => x.id === deviceId);
        failed.push({ hostname: d?.hostname || deviceId, error: e.response?.data?.error || e.message });
      }
    }
    setDeployResults({ success, failed });
    setDeploying(false);
    if (success.length > 0) {
      flash(`✔ "${deployPatch.name}" queued on ${success.length} device(s). Agents will install on next poll.`);
    }
  }

  // Smart search: exact name match first, then partial match across all fields
  const filtered = patches.filter(p => {
    if (!filter) return true;
    const q = filter.toLowerCase().trim();
    // Exact name match — highest priority
    if (p.name?.toLowerCase() === q) return true;
    // Starts-with match on name
    if (p.name?.toLowerCase().startsWith(q)) return true;
    // Substring match across all fields
    return [p.name, p.vendor, p.product, p.version, p.osType, p.category, p.description]
      .join(' ').toLowerCase().includes(q);
  }).sort((a, b) => {
    if (!filter) return 0;
    const q = filter.toLowerCase().trim();
    // Sort exact/starts-with matches to top
    const aScore = a.name?.toLowerCase().startsWith(q) ? 0 : 1;
    const bScore = b.name?.toLowerCase().startsWith(q) ? 0 : 1;
    return aScore - bScore;
  });

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Patch Catalog</div>
          <div className="page-subtitle">{patches.length} patches in catalog</div>
        </div>
        <div className="flex-gap">
          <button className="refresh-btn" onClick={load}>↻ Refresh</button>
          <button className="btn btn-primary" onClick={() => setShowForm(f => !f)}>
            {showForm ? '✕ Cancel' : '+ Add Patch'}
          </button>
        </div>      </div>

      {msg && (
        <div style={{
          background: msgType === 'error' ? '#450a0a' : msgType === 'info' ? '#1e3a5f' : '#14532d',
          color: msgType === 'error' ? '#ef4444' : msgType === 'info' ? '#60a5fa' : '#22c55e',
          padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13
        }}>{msg}</div>
      )}

      {/* Auto-fetch status banner */}
      <div style={{ background: '#1a1d27', border: '1px solid #2d3148', borderRadius: 8, padding: '12px 16px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <span style={{ fontSize: 13, color: '#94a3b8' }}>🔄 Auto-fetch: </span>
          <span style={{ fontSize: 13, color: '#22c55e' }}>Active — checks all vendors every 6 hours</span>
          <span style={{ fontSize: 12, color: '#64748b', marginLeft: 12 }}>Microsoft · Google · Mozilla · Adobe · Dell · HP · Surface</span>
        </div>
        <button
          className="btn btn-primary btn-sm"
          onClick={fetchUpdates}
          disabled={fetching}
          style={{ whiteSpace: 'nowrap' }}
        >
          {fetching ? '⟳ Checking...' : '⬇ Check Now'}
        </button>
      </div>

      {showForm && (
        <div className="table-wrap section-gap" style={{ padding: 20 }}>
          <div className="table-title" style={{ marginBottom: 16 }}>New Patch</div>
          <form onSubmit={save} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {[['name','Name *'],['vendor','Vendor'],['product','Product'],['version','Version *'],['downloadUrl','Download URL'],['command','Command (override)'],['description','Description']].map(([k, label]) => (
              <div key={k} className="form-group" style={k === 'description' || k === 'command' ? { gridColumn: '1/-1' } : {}}>
                <label className="form-label">{label}</label>
                <input className="form-input" value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} required={k === 'name' || k === 'version'} />
              </div>
            ))}
            {[['osType','OS Type', OS_TYPES],['category','Category', CATEGORIES],['severity','Severity', SEVERITIES]].map(([k, label, opts]) => (
              <div key={k} className="form-group">
                <label className="form-label">{label}</label>
                <select className="form-input" value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))}>
                  {opts.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            ))}
            <div style={{ gridColumn: '1/-1' }}>
              <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save Patch'}</button>
            </div>
          </form>
        </div>
      )}

      <div className="table-wrap">
        <div className="table-header">
          <div className="table-title">All Patches</div>
          <input
            className="form-input"
            style={{ width: 220, padding: '6px 10px', fontSize: 12 }}
            placeholder="Search..."
            value={filter}
            onChange={e => setFilter(e.target.value)}
          />
        </div>
        {loading ? <div className="loading">Loading...</div> : (
          <table>
            <thead>
              <tr><th>Patch Name</th><th>Vendor</th><th>Version</th><th>OS</th><th>Category</th><th>Severity</th><th>Install</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={8} className="empty">No patches found.</td></tr>}
              {filtered.map(p => {
                const sc = SEV_COLORS[p.severity] || SEV_COLORS.low;
                return (
                  <tr key={p.id}>
                    <td style={{ maxWidth: 260 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#1e2a3b' }}>{p.name}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>{p.product}</div>
                    </td>
                    <td style={{ fontSize: 13 }}><strong>{p.vendor}</strong></td>
                    <td style={{ fontSize: 12 }}>{p.version}</td>
                    <td><span style={{ fontSize: 11, color: '#94a3b8' }}>{p.osType}</span></td>
                    <td style={{ fontSize: 12 }}>{p.category}</td>
                    <td>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 12, background: sc.bg, color: sc.color }}>
                        {p.severity}
                      </span>
                    </td>
                    <td style={{ fontSize: 11, color: '#64748b', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.downloadUrl ? '⬇ URL' : p.command ? '⚙ cmd' : '—'}
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => openDeploy(p)}
                        style={{ fontSize: 11, padding: '4px 10px' }}
                      >
                        🚀 Deploy
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {/* ── Deploy Modal ────────────────────────────────────────────── */}
      {deployPatch && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)',
          display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ background:'#fff', borderRadius:14, padding:28, width:560,
            maxHeight:'90vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.25)' }}>

            {/* Header */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:18 }}>
              <div>
                <div style={{ fontSize:16, fontWeight:700, color:'#1e2a3b' }}>
                  🚀 Deploy Patch
                </div>
                <div style={{ fontSize:13, color:'#64748b', marginTop:4 }}>{deployPatch.name}</div>
                <div style={{ display:'flex', gap:8, marginTop:6 }}>
                  <span style={{ fontSize:11, background:'#f1f5f9', color:'#64748b',
                    padding:'2px 8px', borderRadius:10 }}>{deployPatch.vendor}</span>
                  <span style={{ fontSize:11, background:'#f1f5f9', color:'#64748b',
                    padding:'2px 8px', borderRadius:10 }}>v{deployPatch.version}</span>
                  <span style={{ fontSize:11, padding:'2px 8px', borderRadius:10,
                    background: (SEV_COLORS[deployPatch.severity]||SEV_COLORS.low).bg,
                    color: (SEV_COLORS[deployPatch.severity]||SEV_COLORS.low).color,
                    fontWeight:700 }}>{deployPatch.severity}</span>
                </div>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={closeDeploy}>✕</button>
            </div>

            {/* Results */}
            {deployResults && (
              <div style={{ marginBottom:16 }}>
                {deployResults.success.length > 0 && (
                  <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0',
                    borderRadius:8, padding:'10px 14px', marginBottom:8 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:'#16a34a' }}>
                      ✔ Queued on {deployResults.success.length} device(s)
                    </div>
                    <div style={{ fontSize:12, color:'#15803d', marginTop:2 }}>
                      {deployResults.success.join(', ')}
                    </div>
                    <div style={{ fontSize:11, color:'#94a3b8', marginTop:4 }}>
                      Agents will install on next poll (within 10 minutes)
                    </div>
                  </div>
                )}
                {deployResults.failed.length > 0 && (
                  <div style={{ background:'#fef2f2', border:'1px solid #fecaca',
                    borderRadius:8, padding:'10px 14px' }}>
                    <div style={{ fontSize:13, fontWeight:700, color:'#dc2626' }}>
                      ✘ Failed on {deployResults.failed.length} device(s)
                    </div>
                    {deployResults.failed.map((f,i) => (
                      <div key={i} style={{ fontSize:11, color:'#dc2626' }}>
                        {f.hostname}: {f.error}
                      </div>
                    ))}
                  </div>
                )}
                <button className="btn btn-ghost btn-sm" style={{ marginTop:10 }} onClick={closeDeploy}>
                  Close
                </button>
              </div>
            )}

            {!deployResults && (
              <>
                {/* Device list */}
                <div style={{ marginBottom:16 }}>
                  <div style={{ fontSize:12, fontWeight:600, color:'#64748b', marginBottom:8 }}>
                    Devices needing this patch
                    <span style={{ fontSize:11, color:'#94a3b8', marginLeft:8 }}>
                      ({selDevIds.length} selected of {devices.length} eligible)
                    </span>
                  </div>

                  {/* Quick select buttons */}
                  <div style={{ display:'flex', gap:6, marginBottom:8, flexWrap:'wrap' }}>
                    <button className="btn btn-sm btn-ghost" style={{ fontSize:11 }}
                      onClick={() => setSelDevIds(devices.filter(d=>d.status==='online').map(d=>d.id))}>
                      ☑ All Online ({devices.filter(d=>d.status==='online').length})
                    </button>
                    <button className="btn btn-sm btn-ghost" style={{ fontSize:11 }}
                      onClick={() => setSelDevIds(devices.map(d=>d.id))}>
                      ☑ All ({devices.length})
                    </button>
                    <button className="btn btn-sm btn-ghost" style={{ fontSize:11 }}
                      onClick={() => setSelDevIds([])}>
                      ☐ Clear
                    </button>
                  </div>

                  <div style={{ border:'1px solid #e8ecf4', borderRadius:8, maxHeight:300, overflowY:'auto' }}>
                    {devices.length === 0 ? (
                      <div style={{ padding:24, textAlign:'center' }}>
                        <div style={{ fontSize:24, marginBottom:8 }}>✅</div>
                        <div style={{ fontSize:13, color:'#16a34a', fontWeight:600 }}>
                          All devices already have this patch applied.
                        </div>
                      </div>
                    ) : devices.map(d => (
                      <label key={d.id} style={{
                        display:'flex', alignItems:'center', gap:10,
                        padding:'9px 14px', cursor:'pointer',
                        background: selDevIds.includes(d.id) ? '#eff4ff' : '#fff',
                        borderBottom:'1px solid #f0f4ff',
                      }}>
                        <input type="checkbox" checked={selDevIds.includes(d.id)}
                          onChange={e => setSelDevIds(prev =>
                            e.target.checked ? [...prev, d.id] : prev.filter(x => x !== d.id)
                          )} />
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:13, fontWeight:600 }}>{d.hostname}</div>
                          <div style={{ fontSize:11, color:'#94a3b8' }}>
                            {d.os}{d.osVersion ? ` · ${d.osVersion}` : ''} · {d.ipAddress || '—'}
                          </div>
                        </div>
                        <span style={{
                          fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:10,
                          background: d.status==='online' ? '#dcfce7' : '#f1f5f9',
                          color:      d.status==='online' ? '#16a34a' : '#94a3b8',
                        }}>
                          {d.status==='online' ? '● Online' : '○ Offline'}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex-gap" style={{ justifyContent:'flex-end' }}>
                  <button className="btn btn-ghost" onClick={closeDeploy}>Cancel</button>
                  <button className="btn btn-primary"
                    onClick={submitDeploy}
                    disabled={deploying || selDevIds.length === 0}>
                    {deploying
                      ? '⟳ Queuing...'
                      : `🚀 Deploy to ${selDevIds.length} Device${selDevIds.length !== 1 ? 's' : ''}`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
